import React from "react";
import { Link } from "react-router-dom";
import { DirectoryHero, CategoryGrid, ListingCard, MapView } from "../components/directory";
import { getFeaturedListings, listings } from "../data";
import FooterOne from '../common/footer/FooterOne';
import HeaderOne from "../common/header/HeaderOne";
import { MapPin, ArrowRight, Star, Clock, Truck } from "lucide-react";
import { SeasonalProduceWidget, RecipeSuggestionsWidget, FarmRecommendationsWidget } from "../components/widgets";

const HomeDefault = () => {
  const featuredListings = getFeaturedListings().slice(0, 6);
  const recentListings = listings.slice(0, 4);

  return (
    <>
      <HeaderOne />
      <DirectoryHero />
      <CategoryGrid />

      {/* Featured Listings Section */}
      <section className="featured-listings-section" style={styles.featuredSection}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="section-header" style={styles.sectionHeader}>
                <span style={styles.sectionTag}>Local Favorites</span>
                <h2 style={styles.sectionTitle}>Featured Farms & Markets</h2>
                <p style={styles.sectionSubtitle}>
                  Discover the best local food sources in Southwest Florida, handpicked by our community
                </p>
              </div>
            </div>
          </div>

          <div className="row">
            {featuredListings.map((listing, index) => (
              <div className="col-lg-4 col-md-6 col-12 mb-4" key={listing.id}>
                <div className="wow fadeInUp" data-wow-delay={`${index * 100}ms`}>
                  <ListingCard listing={listing} variant="grid" />
                </div>
              </div>
            ))}
          </div>

          <div className="row">
            <div className="col-12 text-center mt-4">
              <Link to="/browse" style={styles.viewAllBtn}>
                View All Listings
                <ArrowRight size={18} style={{ marginLeft: '8px' }} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section" style={styles.howItWorksSection}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="section-header text-center" style={styles.sectionHeader}>
                <span style={styles.sectionTagAlt}>Simple & Easy</span>
                <h2 style={styles.sectionTitle}>How Fresh SWFL Works</h2>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <div className="wow fadeInUp" data-wow-delay="0ms">
                <div style={styles.howItWorksCard}>
                  <div style={styles.stepNumber}>1</div>
                  <div style={styles.stepIconWrap}>
                    <MapPin size={40} color="var(--thm-base)" />
                  </div>
                  <h3 style={styles.stepTitle}>Find Local Sources</h3>
                  <p style={styles.stepText}>
                    Search by location, category, or products. Discover farms, markets, and CSAs near you.
                  </p>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <div className="wow fadeInUp" data-wow-delay="100ms">
                <div style={styles.howItWorksCard}>
                  <div style={styles.stepNumber}>2</div>
                  <div style={styles.stepIconWrap}>
                    <Star size={40} color="var(--thm-base)" />
                  </div>
                  <h3 style={styles.stepTitle}>Read Reviews</h3>
                  <p style={styles.stepText}>
                    See ratings and reviews from fellow food lovers. Learn about products and availability.
                  </p>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <div className="wow fadeInUp" data-wow-delay="200ms">
                <div style={styles.howItWorksCard}>
                  <div style={styles.stepNumber}>3</div>
                  <div style={styles.stepIconWrap}>
                    <Truck size={40} color="var(--thm-base)" />
                  </div>
                  <h3 style={styles.stepTitle}>Shop Fresh & Local</h3>
                  <p style={styles.stepText}>
                    Visit farm stands, attend markets, or subscribe to CSA programs. Enjoy farm-fresh food!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI-Powered Features Section */}
      <section className="ai-features-section" style={styles.aiFeaturesSection}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="section-header text-center" style={styles.sectionHeader}>
                <span style={styles.sectionTag}>Smart Recommendations</span>
                <h2 style={styles.sectionTitle}>Personalized For You</h2>
                <p style={styles.sectionSubtitle}>
                  AI-powered features to help you discover the best local food
                </p>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <SeasonalProduceWidget showPeakOnly={true} maxItems={6} />
            </div>

            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <RecipeSuggestionsWidget
                ingredients={['tomatoes', 'basil', 'lettuce']}
                maxRecipes={3}
              />
            </div>

            <div className="col-lg-4 col-md-6 col-12 mb-4">
              <FarmRecommendationsWidget
                allFarms={listings}
                maxRecommendations={3}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Map Preview Section */}
      <section className="map-preview-section" style={styles.mapSection}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5 col-12 mb-4 mb-lg-0">
              <div className="wow fadeInLeft">
                <span style={styles.sectionTag}>Explore The Map</span>
                <h2 style={styles.sectionTitle}>Find Farms Near You</h2>
                <p style={styles.sectionText}>
                  Use our interactive map to discover local food sources in Southwest Florida.
                  Filter by category, see what's open now, and get directions to your favorite spots.
                </p>
                <div style={styles.mapStats}>
                  <div style={styles.statItem}>
                    <span style={styles.statNumber}>{listings.length}+</span>
                    <span style={styles.statLabel}>Local Listings</span>
                  </div>
                  <div style={styles.statItem}>
                    <span style={styles.statNumber}>6</span>
                    <span style={styles.statLabel}>Categories</span>
                  </div>
                  <div style={styles.statItem}>
                    <span style={styles.statNumber}>10+</span>
                    <span style={styles.statLabel}>Cities Covered</span>
                  </div>
                </div>
                <Link to="/map" style={styles.viewAllBtn}>
                  Open Full Map
                  <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                </Link>
              </div>
            </div>
            <div className="col-lg-7 col-12">
              <div className="wow fadeInRight" style={styles.mapPreviewWrap}>
                <MapView />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recently Added Section */}
      <section className="recent-listings-section" style={styles.recentSection}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="section-header" style={styles.sectionHeader}>
                <span style={styles.sectionTag}>New Arrivals</span>
                <h2 style={styles.sectionTitle}>Recently Added</h2>
              </div>
            </div>
          </div>

          {/* Horizontal Scrolling Container */}
          <div style={styles.horizontalScroll}>
            <div style={styles.scrollContainer}>
              {recentListings.map((listing, index) => (
                <div key={listing.id} style={styles.scrollItem}>
                  <div className="wow fadeInUp" data-wow-delay={`${index * 50}ms`}>
                    <ListingCard listing={listing} variant="grid" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="row">
            <div className="col-12 text-center mt-4">
              <Link to="/browse" style={styles.viewAllBtnOutline}>
                Browse All Listings
                <ArrowRight size={18} style={{ marginLeft: '8px' }} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" style={styles.ctaSection}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-12 text-center">
              <div className="wow fadeInUp">
                <h2 style={styles.ctaTitle}>Are You a Local Farm or Market?</h2>
                <p style={styles.ctaText}>
                  Join our directory and connect with customers looking for fresh, local food.
                  It's free to list your business!
                </p>
                <Link to="/contact" style={styles.ctaBtn}>
                  Add Your Listing
                  <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterOne />
    </>
  );
};

const styles = {
  featuredSection: {
    padding: '80px 0',
    backgroundColor: 'var(--thm-bg-cream)',
  },
  sectionHeader: {
    marginBottom: '50px',
    textAlign: 'center',
  },
  sectionTag: {
    display: 'inline-block',
    padding: '6px 16px',
    backgroundColor: 'rgba(45, 90, 39, 0.1)',
    color: 'var(--thm-base)',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '16px',
  },
  sectionTagAlt: {
    display: 'inline-block',
    padding: '6px 16px',
    backgroundColor: 'rgba(212, 136, 58, 0.15)',
    color: 'var(--thm-accent)',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '36px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    marginBottom: '16px',
  },
  sectionSubtitle: {
    fontSize: '18px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    maxWidth: '600px',
    margin: '0 auto',
  },
  sectionText: {
    fontSize: '16px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    lineHeight: '1.7',
    marginBottom: '30px',
  },
  viewAllBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '14px 32px',
    backgroundColor: 'var(--thm-base)',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
  },
  viewAllBtnOutline: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '14px 32px',
    backgroundColor: 'transparent',
    color: 'var(--thm-base)',
    border: '2px solid var(--thm-base)',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: 'var(--thm-font-2)',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
  },
  howItWorksSection: {
    padding: '80px 0',
    backgroundColor: '#fff',
  },
  aiFeaturesSection: {
    padding: '80px 0',
    backgroundColor: 'var(--thm-bg-cream)',
  },
  howItWorksCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '40px 30px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    height: '100%',
    position: 'relative',
    transition: 'transform 0.3s ease',
  },
  stepNumber: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    width: '32px',
    height: '32px',
    backgroundColor: 'var(--thm-bg-sage)',
    color: 'var(--thm-base)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '700',
    fontFamily: 'var(--thm-font-2)',
  },
  stepIconWrap: {
    width: '80px',
    height: '80px',
    backgroundColor: 'var(--thm-bg-sage)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  stepTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: 'var(--thm-black)',
    fontFamily: 'var(--thm-font)',
    marginBottom: '12px',
  },
  stepText: {
    fontSize: '15px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    lineHeight: '1.6',
    margin: 0,
  },
  mapSection: {
    padding: '80px 0',
    backgroundColor: 'var(--thm-bg-sage)',
  },
  mapPreviewWrap: {
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
    height: '450px',
  },
  mapStats: {
    display: 'flex',
    gap: '30px',
    marginBottom: '30px',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  statNumber: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--thm-base)',
    fontFamily: 'var(--thm-font)',
    lineHeight: 1,
  },
  statLabel: {
    fontSize: '14px',
    color: 'var(--thm-gray)',
    fontFamily: 'var(--thm-font-2)',
    marginTop: '4px',
  },
  recentSection: {
    padding: '80px 0',
    backgroundColor: '#fff',
  },
  horizontalScroll: {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
    scrollbarColor: 'var(--thm-base) #E8EDE5',
    paddingBottom: '10px',
    marginLeft: '-15px',
    marginRight: '-15px',
    paddingLeft: '15px',
    paddingRight: '15px',
  },
  scrollContainer: {
    display: 'flex',
    gap: '24px',
    paddingBottom: '10px',
  },
  scrollItem: {
    flex: '0 0 350px',
    minWidth: '350px',
  },
  ctaSection: {
    padding: '80px 0',
    background: 'linear-gradient(135deg, var(--thm-base) 0%, var(--thm-base-light) 100%)',
  },
  ctaTitle: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#fff',
    fontFamily: 'var(--thm-font)',
    marginBottom: '16px',
  },
  ctaText: {
    fontSize: '18px',
    color: 'rgba(255,255,255,0.9)',
    fontFamily: 'var(--thm-font-2)',
    marginBottom: '30px',
    maxWidth: '600px',
    margin: '0 auto 30px',
  },
  ctaBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '16px 36px',
    backgroundColor: '#fff',
    color: 'var(--thm-base)',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '700',
    fontFamily: 'var(--thm-font-2)',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
  },
};

export default HomeDefault;
